import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import TrimSlider from "@/components/TrimSlider";
import EditorMusicPanel, { ClipMoodTagsInput } from "@/components/EditorMusicPanel";
import TitleCardsPanel from "@/components/TitleCardsPanel";
import VideoMetadataSection from "@/components/VideoMetadataSection";
import Player360Editor from "@/components/Player360Editor";
import ModeSelectorCards from "@/components/editor/ModeSelectorCards";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Wand2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Film,
  Scissors,
  Info,
  Clock,
  ChevronRight,
  ChevronLeft,
  ArrowRightLeft,
  Check,
  X,
} from "lucide-react";

function streamUrl(slug) {
  // Editor always uses the mobile-quality variant for fluid scrubbing — the
  // 5760×2880 originals choke even fast laptops on equirectangular textures.
  return `${API_BASE}/videos/${slug}/stream?q=mobile`;
}

function formatTime(seconds) {
  const s = Math.max(0, seconds || 0);
  const m = Math.floor(s / 60);
  const rem = Math.floor(s - m * 60);
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

function formatTimePrecise(seconds) {
  const s = Math.max(0, seconds || 0);
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${m}:${rem.toFixed(1).padStart(4, "0")}`;
}

// Human-readable duration: "12 Sek" for <60s, "1 Min 30 Sek" for >=60s.
// Used in the toolbar and on the Render button so Gerhard sees at a glance
// how long the final export will be.
function formatDurationLabel(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s} Sek`;
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return rem === 0 ? `${m} Min` : `${m} Min ${rem} Sek`;
}

// ---------------- Library card (left column) ----------------
function LibraryCard({ video, usageCount, onAdd, onAddPendingWarn }) {
  const tooShort = !video.duration || video.duration < 0.5;
  const mobileReady = !!video.mobile_ready;
  const reused = usageCount > 0;
  return (
    <div
      data-testid={`editor-library-card-${video.slug}`}
      className="flex gap-3 p-3 rounded-xl border border-[#2a2a30] bg-[#17171a] hover:border-[#3a3a40] transition-colors"
    >
      <div className="relative w-24 h-14 rounded-md overflow-hidden bg-black shrink-0">
        {video.has_thumbnail ? (
          <img
            alt=""
            src={`${API_BASE}/videos/${video.slug}/thumbnail`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <Film size={18} className="text-[#3a3a40]" />
          </div>
        )}
        {video.is_likely_360 && (
          <span className="absolute top-1 right-1 text-[9px] font-semibold uppercase tracking-wider px-1 rounded bg-emerald-500/90 text-white">
            360
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm text-white truncate" title={video.title}>
          {video.title}
        </div>
        <div className="text-[11px] text-[#9a9aa3] mt-0.5">
          {video.duration ? `${formatTime(video.duration)}` : "—"}
          {video.width && video.height && ` · ${video.width}×${video.height}`}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {/* Mobile-ready status badge */}
          {mobileReady ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded"
              data-testid={`editor-library-mobile-ready-${video.slug}`}
            >
              <CheckCircle2 size={9} /> Mobile bereit
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded"
              data-testid={`editor-library-mobile-pending-${video.slug}`}
            >
              <Loader2 size={9} className="animate-spin" /> Wird optimiert
            </span>
          )}
          {/* Usage counter (Phase 7 update) */}
          {usageCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#ff8c66] bg-[#ff6b35]/10 border border-[#ff6b35]/40 px-1.5 py-0.5 rounded"
              data-testid={`editor-library-usage-${video.slug}`}
              title="So oft ist dieses Quellvideo bereits als Clip in der Timeline"
            >
              {usageCount}× in Projekt
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => (mobileReady ? onAdd(video) : onAddPendingWarn(video))}
          disabled={tooShort}
          data-testid={`editor-library-add-${video.slug}`}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#ff8c66] hover:text-[#ff6b35] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={12} /> {reused ? "Erneut als Clip hinzufügen" : "Als Clip hinzufügen"}
        </button>
      </div>
    </div>
  );
}

// ---------------- Help banner (Phase 7) ----------------
function CutHelpBanner() {
  return (
    <div
      className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e] p-4 sm:p-5"
      data-testid="editor-cut-help"
    >
      <div className="flex items-start gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#ff6b35]/10 text-[#ff6b35] shrink-0">
          <Scissors size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white">So schneidest du</h3>
          <ul className="mt-2 space-y-1.5 text-xs text-[#9a9aa3] leading-relaxed">
            <li className="flex items-start gap-2">
              <ChevronRight size={12} className="text-[#ff6b35] mt-0.5 shrink-0" />
              <span>
                Ziehe den orangenen <strong className="text-white">&bdquo;Anfang&ldquo;</strong>-Marker
                nach rechts → der Clip <strong className="text-white">startet später</strong>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronLeft size={12} className="text-[#ff3d6e] mt-0.5 shrink-0" />
              <span>
                Ziehe den pinken <strong className="text-white">&bdquo;Ende&ldquo;</strong>-Marker
                nach links → der Clip <strong className="text-white">endet früher</strong>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Info size={12} className="text-sky-300 mt-0.5 shrink-0" />
              <span>
                Noch einfacher: <strong className="text-white">Video abspielen</strong>,
                an der gewünschten Stelle pausieren, dann auf
                <strong className="text-white"> &bdquo;✂ Anfang hier setzen&ldquo;</strong> oder
                <strong className="text-white"> &bdquo;✂ Ende hier setzen&ldquo;</strong> klicken.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Info size={12} className="text-sky-300 mt-0.5 shrink-0" />
              <span>
                <strong className="text-white">Mitte rausschneiden:</strong> Quellvideo
                zweimal als Clip hinzufügen — Clip 1 endet vor dem unerwünschten Teil,
                Clip 2 beginnt danach.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------- Timeline clip (draggable) ----------------
function TimelineClip({ clip, library, selected, onSelect, onRemove }) {
  const source = library.find((v) => v.id === clip.video_id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: clip.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const duration = clip.end_sec - clip.start_sec;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`editor-timeline-clip-${clip.id}`}
      className={`group shrink-0 w-56 rounded-xl border-2 bg-[#17171a] overflow-hidden cursor-pointer transition-colors ${
        selected ? "border-[#ff6b35] shadow-[0_0_0_3px_rgba(255,107,53,0.15)]" : "border-[#2a2a30] hover:border-[#3a3a40]"
      }`}
      onClick={() => onSelect(clip.id)}
    >
      <div className="relative w-full h-20 bg-black">
        {source?.has_thumbnail ? (
          <img
            alt=""
            src={`${API_BASE}/videos/${source.slug}/thumbnail`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <Film size={20} className="text-[#3a3a40]" />
          </div>
        )}
        <button
          {...attributes}
          {...listeners}
          aria-label="Verschieben"
          data-testid={`editor-clip-drag-${clip.id}`}
          className="absolute top-1 left-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-black/60 text-white cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(clip.id);
          }}
          aria-label="Clip entfernen"
          data-testid={`editor-clip-remove-${clip.id}`}
          className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-black/60 text-red-300 hover:text-red-200 hover:bg-red-500/30"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div className="px-2.5 py-2">
        <div className="text-xs text-white font-medium truncate" title={source?.title || ""}>
          {source?.title || "Quelle fehlt"}
        </div>
        {/* Phase 7: prominent Anfang / Ende / Dauer trio */}
        <div className="mt-1.5 grid grid-cols-3 gap-1 text-[10px] tabular-nums">
          <div data-testid={`editor-clip-start-${clip.id}`}>
            <div className="text-[#6a6a73] uppercase tracking-wider">Anfang</div>
            <div className="text-[#ff8c66] font-semibold">{formatTime(clip.start_sec)}</div>
          </div>
          <div data-testid={`editor-clip-end-${clip.id}`}>
            <div className="text-[#6a6a73] uppercase tracking-wider">Ende</div>
            <div className="text-[#ff6b9a] font-semibold">{formatTime(clip.end_sec)}</div>
          </div>
          <div data-testid={`editor-clip-duration-${clip.id}`}>
            <div className="text-[#6a6a73] uppercase tracking-wider">Dauer</div>
            <div className="text-white font-semibold">{duration.toFixed(1)}s</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Timeline transition between two clips ----------------
const TRANSITION_OPTIONS = [
  { value: "none", label: "Kein Übergang", short: "Schnitt" },
  { value: "crossfade", label: "Sanft überblenden", short: "Sanft" },
  { value: "fade-to-black", label: "Schwarzblende", short: "Schwarz" },
  { value: "fade-to-white", label: "Weißblende", short: "Weiß" },
];

function TimelineTransition({ clip, onChange }) {
  const [open, setOpen] = useState(false);
  const t = clip.transition_after || { type: "none", duration_sec: 1.0 };
  const isActive = t.type !== "none";
  const current = TRANSITION_OPTIONS.find((o) => o.value === t.type) || TRANSITION_OPTIONS[0];

  // Close popover on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const apply = (next) => {
    onChange(clip.id, {
      type: next.type ?? t.type,
      duration_sec: next.duration_sec ?? t.duration_sec,
    });
  };

  return (
    <div
      className="relative shrink-0 self-stretch flex items-center"
      data-testid={`editor-transition-${clip.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Klicken um Übergang zum nächsten Clip zu wählen"
        aria-label={`Übergang zu nächstem Clip: ${current.label}`}
        className={`flex flex-col items-center justify-center w-10 h-20 rounded-md border-2 transition-colors ${
          isActive
            ? "border-sky-400 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25"
            : "border-dashed border-[#3a3a40] bg-[#0d0d0e] text-[#6a6a73] hover:text-white hover:border-[#5a5a60]"
        } ${open ? "ring-2 ring-sky-400/40" : ""}`}
        data-testid={`editor-transition-button-${clip.id}`}
      >
        <ArrowRightLeft size={16} />
        <span className="text-[9px] uppercase tracking-wider font-semibold mt-1">
          {current.short}
        </span>
      </button>

      {open && (
        <>
          {/* Backdrop to dismiss on outside click. */}
          <div
            className="fixed inset-0 z-20"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute z-30 top-[88px] left-1/2 -translate-x-1/2 w-64 rounded-xl border border-[#2a2a30] bg-[#0d0d0e] shadow-[0_12px_30px_rgba(0,0,0,0.5)] p-3"
            role="dialog"
            aria-label="Übergang wählen"
            data-testid={`editor-transition-popover-${clip.id}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-[#6a6a73] mb-2 font-medium">
              Übergang zum nächsten Clip
            </div>
            <div className="flex flex-col gap-1">
              {TRANSITION_OPTIONS.map((opt) => {
                const active = opt.value === t.type;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      apply({ type: opt.value });
                    }}
                    className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-sm text-left transition-colors ${
                      active
                        ? "bg-sky-500/15 text-sky-100"
                        : "text-[#d4d4d8] hover:bg-[#1f1f23]"
                    }`}
                    data-testid={`editor-transition-option-${clip.id}-${opt.value}`}
                  >
                    <span>{opt.label}</span>
                    {active && <Check size={14} className="text-sky-300 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-[#2a2a30]">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#6a6a73] mb-1.5 font-medium">
                <span>Dauer</span>
                <span className="text-white normal-case tracking-normal tabular-nums">
                  {t.duration_sec.toFixed(1)} s
                </span>
              </div>
              <input
                type="range"
                min={0.5}
                max={2.0}
                step={0.1}
                value={t.duration_sec}
                disabled={!isActive}
                onChange={(e) =>
                  apply({ duration_sec: parseFloat(e.target.value) })
                }
                className="w-full accent-sky-400 disabled:opacity-40"
                data-testid={`editor-transition-duration-${clip.id}`}
              />
              {!isActive && (
                <p className="text-[10px] text-[#6a6a73] mt-1">
                  Wähle erst einen Effekt, um die Dauer zu ändern.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- Main editor page ----------------
export default function EditorEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const videoSlug = searchParams.get("slug");
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [savingPatch, setSavingPatch] = useState(false);
  const [rendering, setRendering] = useState(false);
  // Render progress state — populated by the /projects/{id}/status poll while
  // rendering. `renderProgress` is 0..100, `renderStage` is a stringy label
  // like "Vorbereiten" / "Encoding" / "Finalisieren".
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderStage, setRenderStage] = useState(null);
  // Wall-clock timestamp of the most recent startRender() invocation. Also
  // seeded from project.updated_at on mount when the project comes back with
  // status=rendering (i.e. a render kicked off in another tab/session is
  // already in flight). Used to expose the "Abbrechen" escape hatch after 20 min.
  const [renderStartedAt, setRenderStartedAt] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // Phase 11K Part 2 — Modus-Wechsel + Precheck-Overlay
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [modeDialogTarget, setModeDialogTarget] = useState(null); // proposed new mode
  const [modeDialogBusy, setModeDialogBusy] = useState(false);
  const [modePurgePrompt, setModePurgePrompt] = useState(null); // {target, affected}
  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [precheckData, setPrecheckData] = useState(null);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [precheckError, setPrecheckError] = useState(null);
  const [precheckSwitching, setPrecheckSwitching] = useState(false);
  // Session-local dismiss for the non-fatal render_warning banner. Reset on
  // every render start so a fresh warning after a re-render shows again.
  const [warningDismissed, setWarningDismissed] = useState(false);

  const trimDraftRef = useRef(null);
  const [trimDraft, setTrimDraft] = useState(null);

  // Imperative handle on Player360Editor for "Anfang/Ende hier setzen".
  const playerRef = useRef(null);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pRes, lRes] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/videos`, { params: { type: "original" } }),
        ]);
        if (cancelled) return;
        setProject(pRes.data);
        setTitle(pRes.data.title);
        setLibrary(lRes.data.filter((v) => v.duration && v.duration >= 0.5));
        if (pRes.data.clips.length > 0) setSelectedClipId(pRes.data.clips[0].id);
        if (pRes.data.status === "rendering") setRendering(true);
      } catch (err) {
        const code = err.response?.status;
        if (code === 404) {
          toast.error("Projekt nicht gefunden");
          navigate("/admin/editor");
          return;
        }
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // Poll the library every 4s while we're on the page so freshly-finished
  // mobile transcodes flip the "Wird optimiert" badge to "Mobile bereit"
  // without a manual refresh.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/videos`, { params: { type: "original" } });
        setLibrary(data.filter((v) => v.duration && v.duration >= 0.5));
      } catch {
        /* transient — ignore */
      }
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const clipsToPayload = useCallback((clips) =>
    clips.map((c) => ({
      id: c.id,
      video_id: c.video_id,
      start_sec: c.start_sec,
      end_sec: c.end_sec,
      transition_after: c.transition_after
        ? {
            type: c.transition_after.type,
            duration_sec: c.transition_after.duration_sec,
          }
        : { type: "none", duration_sec: 1.0 },
    })), []);

  const persistClips = useCallback(async (nextClips, options = {}) => {
    const { selectByIndex } = options;
    setSavingPatch(true);
    try {
      const { data } = await api.patch(`/projects/${id}`, {
        clips: clipsToPayload(nextClips),
      });
      setProject(data);
      if (typeof selectByIndex === "number" && data.clips[selectByIndex]) {
        setSelectedClipId(data.clips[selectByIndex].id);
      } else if (selectedClipId && !data.clips.find((c) => c.id === selectedClipId)) {
        setSelectedClipId(data.clips[0]?.id || null);
      }
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      return null;
    } finally {
      setSavingPatch(false);
    }
  }, [id, clipsToPayload, selectedClipId]);

  const persistTitle = useCallback(async () => {
    if (!project || title.trim() === project.title) return;
    setSavingPatch(true);
    try {
      const { data } = await api.patch(`/projects/${id}`, { title: title.trim() || "Projekt" });
      setProject(data);
      setTitle(data.title);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPatch(false);
    }
  }, [id, title, project]);

  const onAddClip = async (video) => {
    if (!project) return;
    const defaultEnd = Math.min(video.duration || 5, 5);
    const newClips = [
      ...project.clips,
      { id: undefined, video_id: video.id, start_sec: 0, end_sec: defaultEnd },
    ];
    await persistClips(newClips, { selectByIndex: newClips.length - 1 });
  };

  const onAddPendingWarn = (video) => {
    toast.warning("Datei wird noch optimiert", {
      description: "Du kannst sie schon zur Timeline hinzufügen — die Vorschau ruckelt evtl. bis das grüne Mobile-bereit-Badge erscheint.",
      action: { label: "Trotzdem hinzufügen", onClick: () => onAddClip(video) },
    });
  };

  const onRemoveClip = async (clipId) => {
    if (!project) return;
    const next = project.clips.filter((c) => c.id !== clipId);
    const data = await persistClips(next);
    if (data && selectedClipId === clipId) {
      setSelectedClipId(data.clips[0]?.id || null);
    }
  };

  const onTransitionChange = async (clipId, transition) => {
    if (!project) return;
    const next = project.clips.map((c) =>
      c.id === clipId
        ? { ...c, transition_after: { ...(c.transition_after || {}), ...transition } }
        : c,
    );
    await persistClips(next);
  };

  const onClipMoodTagsChange = async (clipId, tags) => {
    if (!project) return;
    const next = project.clips.map((c) =>
      c.id === clipId ? { ...c, mood_tags: tags } : c,
    );
    await persistClips(next);
  };

  const onPatchMusic = async (partial) => {
    if (!project) return;
    setSavingPatch(true);
    try {
      const merged = { ...(project.music || {}), ...partial };
      const { data } = await api.patch(`/projects/${id}`, { music: merged });
      setProject(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPatch(false);
    }
  };

  const onPatchClipsMusic = async (suggestions) => {
    if (!project) return;
    const byClipId = new Map(suggestions.map((s) => [s.clip_id, s.track_id]));
    const next = project.clips.map((c) =>
      byClipId.has(c.id) ? { ...c, music_track_id: byClipId.get(c.id) } : c,
    );
    // Also promote the most-common suggested track to the PROJECT-level slot
    // — that is the field the renderer actually reads. Without this, all
    // auto-matched projects rendered silently (Bug: prod slug zkxm5bc5).
    const votes = new Map();
    for (const s of suggestions) {
      if (!s.track_id) continue;
      votes.set(s.track_id, (votes.get(s.track_id) || 0) + 1);
    }
    let bestTrackId = null;
    let bestScore = 0;
    for (const [tid, score] of votes.entries()) {
      if (score > bestScore) {
        bestScore = score;
        bestTrackId = tid;
      }
    }
    // Persist clips first, then (only if we picked a track) merge into the
    // global music block so downstream music mix picks it up. Falls the
    // user had a project-level track already, keep the per-project override
    // — auto-match refines suggestions, it doesn't overwrite explicit picks.
    await persistClips(next);
    const globalMusic = project.music || {};
    if (bestTrackId && !globalMusic.track_id) {
      const merged = {
        ...globalMusic,
        track_id: bestTrackId,
        volume_pct: globalMusic.volume_pct ?? 60,
        fade_in_sec: globalMusic.fade_in_sec ?? 1.0,
        fade_out_sec: globalMusic.fade_out_sec ?? 2.0,
        auto_duck: globalMusic.auto_duck ?? true,
      };
      const { data } = await api.patch(`/projects/${id}`, { music: merged });
      setProject(data);
    }
  };

  const onPatchTitleCards = async (cards) => {
    if (!project) return;
    setSavingPatch(true);
    try {
      const { data } = await api.patch(`/projects/${id}`, { title_cards: cards });
      setProject(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPatch(false);
    }
  };

  const onReorder = async (oldIdx, newIdx) => {
    if (!project) return;
    const next = arrayMove(project.clips, oldIdx, newIdx);
    await persistClips(next);
  };

  const onTrimChange = useCallback(({ start, end }) => {
    if (!selectedClipId) return;
    trimDraftRef.current = { start, end };
    setTrimDraft({ start, end });
  }, [selectedClipId]);

  const onTrimCommit = useCallback(async () => {
    if (!project || !selectedClipId || !trimDraftRef.current) return;
    const { start, end } = trimDraftRef.current;
    const next = project.clips.map((c) =>
      c.id === selectedClipId ? { ...c, start_sec: start, end_sec: end } : c
    );
    trimDraftRef.current = null;
    setTrimDraft(null);
    await persistClips(next);
  }, [project, selectedClipId, persistClips]);

  // Selected clip + source.
  const selectedClip = useMemo(() => {
    if (!project || !selectedClipId) return null;
    return project.clips.find((c) => c.id === selectedClipId) || null;
  }, [project, selectedClipId]);

  const selectedSource = useMemo(() => {
    if (!selectedClip) return null;
    return library.find((v) => v.id === selectedClip.video_id) || null;
  }, [selectedClip, library]);

  // Total wall-clock duration of the final export. Sum of every clip's
  // (end_sec - start_sec), MINUS the xfade overlap per transition (xfade
  // shortens the timeline by `duration` seconds). This matches the
  // total_render_dur we pass to ffmpeg on the backend, so what the user
  // sees in the header is what actually gets encoded.
  const totalDurationSec = useMemo(() => {
    if (!project?.clips?.length) return 0;
    const clipSum = project.clips.reduce(
      (acc, c) => acc + Math.max(0, (c.end_sec || 0) - (c.start_sec || 0)),
      0,
    );
    const overlap = (project.transitions || []).reduce(
      (acc, t) => (t?.name && t.name !== "none" ? acc + (t.duration_sec || 0) : acc),
      0,
    );
    return Math.max(0, clipSum - overlap);
  }, [project?.clips, project?.transitions]);

  const effectiveStart = trimDraft?.start ?? selectedClip?.start_sec ?? 0;
  const effectiveEnd = trimDraft?.end ?? selectedClip?.end_sec ?? 0;

  // ---------------- "Anfang/Ende hier setzen" actions ----------------
  const setStartHere = async () => {
    if (!project || !selectedClip || !playerRef.current) return;
    const t = Math.max(0, playerRef.current.getCurrentTime());
    const minStartMargin = 0.5;
    if (t >= selectedClip.end_sec - minStartMargin) {
      toast.error("Aktuelle Position liegt zu nah am Ende — Anfang nicht gesetzt.");
      return;
    }
    const nextStart = Math.min(t, Math.max(0, (selectedSource?.duration || t) - minStartMargin));
    const next = project.clips.map((c) =>
      c.id === selectedClip.id ? { ...c, start_sec: nextStart } : c
    );
    setTrimDraft({ start: nextStart, end: selectedClip.end_sec });
    trimDraftRef.current = { start: nextStart, end: selectedClip.end_sec };
    const data = await persistClips(next);
    if (data) {
      trimDraftRef.current = null;
      setTrimDraft(null);
      toast.success(`Anfang gesetzt auf ${formatTimePrecise(nextStart)}`);
    }
  };

  const setEndHere = async () => {
    if (!project || !selectedClip || !playerRef.current) return;
    const t = playerRef.current.getCurrentTime();
    const minEndMargin = 0.5;
    if (t <= selectedClip.start_sec + minEndMargin) {
      toast.error("Aktuelle Position liegt zu nah am Anfang — Ende nicht gesetzt.");
      return;
    }
    const maxEnd = selectedSource?.duration || (selectedClip.start_sec + 1);
    const nextEnd = Math.min(t, maxEnd);
    const next = project.clips.map((c) =>
      c.id === selectedClip.id ? { ...c, end_sec: nextEnd } : c
    );
    setTrimDraft({ start: selectedClip.start_sec, end: nextEnd });
    trimDraftRef.current = { start: selectedClip.start_sec, end: nextEnd };
    const data = await persistClips(next);
    if (data) {
      trimDraftRef.current = null;
      setTrimDraft(null);
      toast.success(`Ende gesetzt auf ${formatTimePrecise(nextEnd)}`);
    }
  };

  // ---------------- Render flow ----------------
  const renderPoll = useRef(null);
  const stopPolling = () => {
    if (renderPoll.current) {
      clearInterval(renderPoll.current);
      renderPoll.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const pollStatus = useCallback(() => {
    stopPolling();
    renderPoll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${id}/status`);
        // Live progress feed for the UI. `render_progress` is 0..99 while
        // ffmpeg is grinding, then flips to 100 on the success write.
        if (typeof data.render_progress === "number") {
          setRenderProgress(data.render_progress);
        }
        if (data.render_stage !== undefined) {
          setRenderStage(data.render_stage || null);
        }
        // Anchor the Cancel-button grace period on the SERVER's
        // render_last_started_at — surviving container restarts
        // (which is why we can't just use client Date.now()).
        if (data.render_last_started_at) {
          const t = Date.parse(data.render_last_started_at);
          if (!Number.isNaN(t)) setRenderStartedAt(t);
        }
        if (data.status === "rendered") {
          setRenderProgress(100);
          setRenderStage(null);
          stopPolling();
          setRendering(false);
          const fresh = await api.get(`/projects/${id}`);
          setProject(fresh.data);
          toast.success('Render fertig — Video in „Bearbeitete" verfügbar');
        } else if (data.status === "failed" || data.status === "cancelled") {
          stopPolling();
          setRendering(false);
          setRenderStage(null);
          const fresh = await api.get(`/projects/${id}`);
          setProject(fresh.data);
          if (data.status === "cancelled") {
            toast.message("Render abgebrochen");
          } else {
            toast.error(`Render fehlgeschlagen: ${data.render_error || "Unbekannt"}`);
          }
        }
      } catch {
        /* transient */
      }
    }, 2000);
  }, [id]);

  const startRender = async () => {
    if (!project) return;
    if (project.clips.length === 0) {
      toast.error("Mindestens ein Clip in der Timeline nötig");
      return;
    }
    setRendering(true);
    setRenderProgress(0);
    setRenderStage("Vorbereiten");
    setRenderStartedAt(Date.now());
    // Fresh render → wipe any dismissed-warning state so the next
    // render_warning (or lack thereof) surfaces cleanly.
    setWarningDismissed(false);
    try {
      await api.post(`/projects/${id}/render`);
      pollStatus();
    } catch (err) {
      setRendering(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    if (project?.status === "rendering" && !renderPoll.current) {
      setRendering(true);
      // Seed the render-started clock from render_last_started_at (or fall
      // back to updated_at for pre-migration projects) so the Cancel button
      // grace period survives page reloads.
      if (!renderStartedAt) {
        const stamp = project.render_last_started_at || project.updated_at;
        if (stamp) {
          const t = Date.parse(stamp);
          if (!Number.isNaN(t)) setRenderStartedAt(t);
        }
      }
      // Seed initial progress from the doc if backend already recorded some.
      if (typeof project.render_progress === "number") {
        setRenderProgress(project.render_progress);
      }
      if (project.render_stage) setRenderStage(project.render_stage);
      pollStatus();
    }
  }, [project?.status, project?.render_last_started_at, project?.updated_at, project?.render_progress, project?.render_stage, renderStartedAt, pollStatus]);

  const cancelRender = async () => {
    if (!window.confirm("Rendering wirklich abbrechen?")) return;
    setCancelling(true);
    try {
      await api.post(`/projects/${id}/cancel`);
      stopPolling();
      setRendering(false);
      setRenderProgress(0);
      setRenderStage(null);
      setRenderStartedAt(null);
      const fresh = await api.get(`/projects/${id}`);
      setProject(fresh.data);
      toast.message("Render abgebrochen. Du kannst neu rendern.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Abbruch fehlgeschlagen",
      );
    } finally {
      setCancelling(false);
    }
  };

  const retryRender = async () => {
    // Reset attempts + last_started server-side and re-enqueue a fresh
    // render. Only reachable when status is failed/cancelled — the button
    // is only rendered in that case.
    setRendering(true);
    setRenderProgress(0);
    setRenderStage("Vorbereiten");
    setRenderStartedAt(Date.now());
    try {
      await api.post(`/projects/${id}/retry`);
      const fresh = await api.get(`/projects/${id}`);
      setProject(fresh.data);
      pollStatus();
      toast.success("Render gestartet");
    } catch (err) {
      setRendering(false);
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Neustart fehlgeschlagen",
      );
    }
  };

  // ---------- Mode switch (Phase 11K Part 2) ----------
  const openModeDialog = () => {
    if (rendering) return;
    setModeDialogTarget(project?.render_mode || "full");
    setModeDialogOpen(true);
  };
  const closeModeDialog = () => {
    setModeDialogOpen(false);
    setModeDialogTarget(null);
    setModeDialogBusy(false);
  };
  // Actually PATCH the mode. If the backend responds 409 with
  // requires_confirm_purge, we surface the numbers to the confirm dialog
  // and this same function is called again with confirm=true.
  const applyModeChange = async (target, confirm) => {
    setModeDialogBusy(true);
    try {
      const { data } = await api.patch(`/projects/${id}/mode`, {
        render_mode: target,
        confirm_purge: !!confirm,
      });
      setProject(data);
      const label =
        target === "fastcut"
          ? "Schnellschnitt"
          : target === "hybrid"
            ? "Übergänge"
            : "Voll-Editor";
      toast.success(`Modus zu ${label} gewechselt`);
      closeModeDialog();
      setModePurgePrompt(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend gives us a structured payload when a purge is required.
      if (
        err.response?.status === 409 &&
        detail &&
        typeof detail === "object" &&
        detail.requires_confirm_purge
      ) {
        setModePurgePrompt({ target, affected: detail.affected || {} });
        setModeDialogBusy(false);
        return;
      }
      toast.error(
        formatApiErrorDetail(detail) || err.message || "Modus-Wechsel fehlgeschlagen",
      );
      setModeDialogBusy(false);
    }
  };

  // ---------- Precheck overlay (Phase 11K Part 2) ----------
  const totalClipDurSec = (project?.clips || []).reduce(
    (acc, c) => acc + Math.max(0, (c.end_sec || 0) - (c.start_sec || 0)),
    0,
  );
  // Rough render-time estimates fed to the precheck overlay. Deliberately
  // pessimistic on full so users aren't surprised.
  const estimateSecondsFor = (mode, clipDurSec) => {
    if (mode === "fastcut") return Math.max(10, clipDurSec * 0.3);
    if (mode === "hybrid") return Math.max(60, clipDurSec * 1.5);
    return Math.max(180, clipDurSec * 8);
  };
  const formatEstimate = (secs) => {
    if (secs < 60) return `~${Math.round(secs)} Sekunden`;
    const mins = Math.round(secs / 60);
    return `~${mins} Minute${mins === 1 ? "" : "n"}`;
  };
  const openPrecheckOverlay = async () => {
    if (rendering) return;
    setPrecheckOpen(true);
    setPrecheckLoading(true);
    setPrecheckData(null);
    setPrecheckError(null);
    try {
      const { data } = await api.get(`/projects/${id}/precheck`);
      setPrecheckData(data);
    } catch (err) {
      // Keep the overlay open with an inline error + retry button so the
      // user doesn't have to click Rendern again from scratch. Falls back
      // to compatible=false semantics — Rendern-Start-Button stays hidden.
      setPrecheckError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Kompatibilitäts-Prüfung fehlgeschlagen.",
      );
    } finally {
      setPrecheckLoading(false);
    }
  };
  const closePrecheck = () => {
    setPrecheckOpen(false);
    setPrecheckData(null);
    setPrecheckError(null);
    setPrecheckSwitching(false);
  };
  const retryPrecheck = async () => {
    setPrecheckLoading(true);
    setPrecheckError(null);
    setPrecheckData(null);
    try {
      const { data } = await api.get(`/projects/${id}/precheck`);
      setPrecheckData(data);
    } catch (err) {
      setPrecheckError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Kompatibilitäts-Prüfung fehlgeschlagen.",
      );
    } finally {
      setPrecheckLoading(false);
    }
  };
  const switchToFullFromPrecheck = async () => {
    setPrecheckSwitching(true);
    try {
      // Full never purges, so confirm_purge is a no-op here.
      const { data } = await api.patch(`/projects/${id}/mode`, {
        render_mode: "full",
        confirm_purge: false,
      });
      setProject(data);
      // Re-run the precheck against the new mode (should always be green
      // for full — but we let the user see that).
      const { data: pc } = await api.get(`/projects/${id}/precheck`);
      setPrecheckData(pc);
      toast.success("Modus zu Voll-Editor gewechselt");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Modus-Wechsel fehlgeschlagen",
      );
    } finally {
      setPrecheckSwitching(false);
    }
  };
  const confirmAndStartRender = () => {
    closePrecheck();
    startRender();
  };

  // ---------------- DnD sensors ----------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !project) return;
    const oldIdx = project.clips.findIndex((c) => c.id === active.id);
    const newIdx = project.clips.findIndex((c) => c.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(project.clips, oldIdx, newIdx).map((c, i) => ({ ...c, order: i }));
    setProject({ ...project, clips: next });
    onReorder(oldIdx, newIdx);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white">
        <Topbar />
        <main className="max-w-6xl mx-auto px-6 py-20 text-center text-[#9a9aa3]">
          <Loader2 className="mx-auto animate-spin" size={32} />
          <p className="mt-3 text-sm">Lade Projekt…</p>
        </main>
      </div>
    );
  }

  if (!project) return null;

  const outputReady = project.status === "rendered" && project.output_slug;
  const selectedSourceMobileReady = selectedSource ? !!selectedSource.mobile_ready : true;

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Topbar />

      {/* Header */}
      <div className="border-b border-[#2a2a30] bg-[#0a0a0b]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <Link
            to="/admin/editor"
            className="inline-flex items-center gap-1.5 text-sm text-[#9a9aa3] hover:text-white"
            data-testid="editor-back-link"
          >
            <ArrowLeft size={16} /> Editor
          </Link>
          <div className="h-5 w-px bg-[#2a2a30]" />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={persistTitle}
            placeholder="Projekt-Titel"
            data-testid="editor-title-input"
            className="flex-1 min-w-[180px] bg-transparent text-lg font-semibold text-white outline-none border-b border-transparent focus:border-[#ff6b35] py-1"
          />
          {savingPatch && (
            <span className="inline-flex items-center gap-1 text-xs text-[#9a9aa3]">
              <Loader2 size={12} className="animate-spin" /> Speichere
            </span>
          )}
          {project.clips.length > 0 && (
            <span
              data-testid="editor-total-duration"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#c9c9d1] bg-[#17171a] border border-[#2a2a30] px-2.5 py-1.5 rounded-md"
              title="Voraussichtliche Länge des fertigen Videos"
            >
              <Clock size={12} className="text-[#9a9aa3]" />
              Gesamtdauer: {formatDurationLabel(totalDurationSec)}
            </span>
          )}
          {/* Render-mode badge — the pipeline this project will use and a
              rough estimate of how long it will take. Klick öffnet den
              Modus-Wechsel-Dialog. */}
          {project.render_mode && (
            <button
              type="button"
              onClick={openModeDialog}
              disabled={rendering}
              data-testid={`editor-mode-badge-${project.render_mode}`}
              title="Modus ändern"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${
                rendering ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:brightness-125"
              } ${
                project.render_mode === "fastcut"
                  ? "text-emerald-200 bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15"
                  : project.render_mode === "hybrid"
                    ? "text-sky-200 bg-sky-500/10 border-sky-500/30 hover:bg-sky-500/15"
                    : "text-amber-200 bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/15"
              }`}
            >
              {project.render_mode === "fastcut" && "\u26A1 Schnellschnitt · ~15 Sek"}
              {project.render_mode === "hybrid" && "\uD83D\uDE80 Übergänge · ~90 Sek"}
              {project.render_mode === "full" && "\uD83C\uDFAC Voll-Editor · ~5-15 Min"}
            </button>
          )}
          {outputReady && (
            <Link
              to={`/v/${project.output_slug}?t=${Date.now()}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 rounded-md"
              data-testid="editor-open-output"
            >
              <CheckCircle2 size={14} /> Output ansehen
            </Link>
          )}
          <button
            type="button"
            onClick={openPrecheckOverlay}
            disabled={rendering || project.clips.length === 0}
            data-testid="editor-render-button"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rendering ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Rendere…
              </>
            ) : (
              <>
                <Wand2 size={14} />
                {totalDurationSec > 0
                  ? `Rendern (${formatDurationLabel(totalDurationSec)} Video)`
                  : "Rendern"}
              </>
            )}
          </button>
        </div>
        {rendering && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3" data-testid="editor-render-progress">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="text-xs font-medium text-[#c9c9d1]">
                {renderStage || "Rendern läuft"}
              </span>
              <span className="text-xs tabular-nums text-[#9a9aa3]">
                {Math.max(0, Math.min(100, renderProgress))}%
              </span>
              {renderStartedAt && Date.now() - renderStartedAt > 20 * 60 * 1000 && (
                <button
                  type="button"
                  onClick={cancelRender}
                  disabled={cancelling}
                  data-testid="editor-cancel-render"
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-red-300 hover:text-red-200 border border-red-500/30 hover:bg-red-500/10 px-2.5 py-1 rounded-md transition-colors disabled:opacity-60"
                  title="Rendering abbrechen (läuft schon > 20 Min)"
                >
                  {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                  Abbrechen
                </button>
              )}
            </div>
            <div className="h-2 rounded-full bg-[#1f1f23] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] transition-[width] duration-500 ease-out"
                style={{ width: `${Math.max(0, Math.min(100, renderProgress))}%` }}
              />
            </div>
          </div>
        )}
        {project.render_error && project.status === "failed" && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
            <div
              className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 px-4 py-3 rounded-md"
              data-testid="editor-render-error"
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span className="break-words">{project.render_error}</span>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={retryRender}
                disabled={rendering}
                data-testid="editor-retry-render-btn"
                className="shrink-0"
              >
                Erneut versuchen
              </Button>
            </div>
          </div>
        )}
        {project.render_warning && project.status === "rendered" && !warningDismissed && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
            <div
              className="flex items-start gap-3 text-sm text-amber-100 bg-amber-500/10 border border-amber-500/40 px-4 py-3 rounded-md"
              data-testid="editor-render-warning-banner"
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-300" />
              <span className="break-words flex-1 min-w-0">{project.render_warning}</span>
              <button
                type="button"
                onClick={() => setWarningDismissed(true)}
                className="shrink-0 -m-1 p-1 rounded-md text-amber-200 hover:text-white hover:bg-amber-500/20 transition-colors"
                aria-label="Warnung ausblenden"
                data-testid="editor-render-warning-dismiss-btn"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        {project.source_partially_deleted && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
            <div
              className="inline-flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 px-3 py-2 rounded-md"
              data-testid="editor-source-deleted-hint"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                Ein verwendetes Quellvideo wurde gelöscht — das gerenderte Output-Video
                bleibt erhalten, aber ein erneuter Render ist nicht mehr möglich.
              </span>
            </div>
          </div>
        )}
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Phase 11 — Unified editor: video-level metadata section, only shown
            when opened from the library (with ?slug=…). Contains title / slug /
            visibility / text overlays / POI arrows in tabs. */}
        {videoSlug && (
          <VideoMetadataSection videoSlug={videoSlug} />
        )}

        {/* Help banner — always visible after user feedback "Trim-UI ist unklar" */}
        <CutHelpBanner />

        {/* Top: library + preview */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
          {/* Library */}
          <aside
            className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e] p-3 max-h-[60vh] lg:max-h-[560px] overflow-y-auto"
            data-testid="editor-library"
          >
            <div className="px-1 pb-2 text-xs uppercase tracking-wider text-[#9a9aa3]">Bibliothek</div>
            {library.length === 0 ? (
              <div className="text-sm text-[#9a9aa3] px-2 py-6">
                Keine Quell-Videos. Lade zuerst ein Video hoch.
              </div>
            ) : (
              <div className="space-y-2">
                {library.map((v) => {
                  const usageCount = project.clips.filter((c) => c.video_id === v.id).length;
                  return (
                    <LibraryCard
                      key={v.id}
                      video={v}
                      usageCount={usageCount}
                      onAdd={onAddClip}
                      onAddPendingWarn={onAddPendingWarn}
                    />
                  );
                })}
              </div>
            )}
          </aside>

          {/* Preview */}
          <section className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e] p-3" data-testid="editor-preview">
            {selectedSource ? (
              <Player360Editor
                ref={playerRef}
                key={selectedSource.slug}
                src={streamUrl(selectedSource.slug)}
                startSec={effectiveStart}
                endSec={effectiveEnd}
                mobileReady={selectedSourceMobileReady}
              />
            ) : (
              <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden grid place-items-center text-center text-[#9a9aa3]">
                <div>
                  <Film size={28} className="mx-auto opacity-50" />
                  <p className="text-sm mt-2">Wähle oder füge einen Clip in der Timeline hinzu.</p>
                </div>
              </div>
            )}

            {selectedClip && (
              <>
                {/* Phase 7: prominent Anfang/Ende setzen buttons */}
                <div
                  className="mt-3 flex flex-wrap items-center gap-2"
                  data-testid="editor-set-here-actions"
                >
                  <button
                    type="button"
                    onClick={setStartHere}
                    data-testid="editor-set-start-here"
                    className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-[#ff6b35]/15 hover:bg-[#ff6b35]/25 border border-[#ff6b35]/50 text-[#ff8c66] text-xs font-semibold transition-colors"
                  >
                    <Scissors size={13} />
                    Anfang hier setzen
                  </button>
                  <button
                    type="button"
                    onClick={setEndHere}
                    data-testid="editor-set-end-here"
                    className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-[#ff3d6e]/15 hover:bg-[#ff3d6e]/25 border border-[#ff3d6e]/50 text-[#ff6b9a] text-xs font-semibold transition-colors"
                  >
                    <Scissors size={13} />
                    Ende hier setzen
                  </button>
                  <span className="ml-auto text-[11px] text-[#6a6a73] inline-flex items-center gap-1">
                    <Clock size={11} />
                    Tipp: pausiere an der richtigen Stelle, dann klicken
                  </span>
                </div>

                <div className="mt-4">
                  <TrimSlider
                    duration={selectedSource?.duration || effectiveEnd}
                    start={effectiveStart}
                    end={effectiveEnd}
                    onChange={onTrimChange}
                    onChangeCommitted={onTrimCommit}
                  />
                </div>
              </>
            )}
          </section>
        </div>

        {/* Bottom: timeline */}
        <section data-testid="editor-timeline-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-wider text-[#9a9aa3] font-medium">Timeline</h2>
            <span className="text-xs text-[#5a5a60]" data-testid="editor-clip-count">
              {project.clips.length} {project.clips.length === 1 ? "Clip" : "Clips"}
            </span>
          </div>

          {project.clips.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-[#2a2a30] bg-[#17171a]/40 p-8 text-center"
              data-testid="editor-timeline-empty"
            >
              <p className="text-sm text-[#9a9aa3]">
                Ziehe Clips aus der Bibliothek (links) hierher, schiebe sie in der gewünschten Reihenfolge
                und trimme mit dem Slider rechts.
              </p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={project.clips.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div
                  className="flex gap-3 overflow-x-auto pb-3 -mx-4 sm:-mx-6 px-4 sm:px-6 snap-x"
                  data-testid="editor-timeline-list"
                >
                  {project.clips.map((c, idx) => (
                    <div
                      key={c.id}
                      className="flex items-stretch gap-3"
                      data-testid={`editor-timeline-pair-${idx}`}
                    >
                      <div className="flex flex-col gap-1.5">
                        <TimelineClip
                          clip={c}
                          library={library}
                          selected={c.id === selectedClipId}
                          onSelect={setSelectedClipId}
                          onRemove={onRemoveClip}
                        />
                        <ClipMoodTagsInput
                          clip={c}
                          onChange={(tags) => onClipMoodTagsChange(c.id, tags)}
                          testid={`editor-clip-tags-${idx}`}
                        />
                      </div>
                      {idx < project.clips.length - 1 && project.render_mode !== "fastcut" && (
                        <TimelineTransition
                          clip={c}
                          onChange={onTransitionChange}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>

        {project && project.clips.length > 0 && (
          <EditorMusicPanel
            project={project}
            onPatchMusic={onPatchMusic}
            onPatchClipsMusic={onPatchClipsMusic}
          />
        )}

        {/* Title-cards panel: only available in `full` render mode.
            Fastcut+Hybrid drop these features to keep the render pipeline
            re-encode-free (title cards need libx264). */}
        {project && project.render_mode === "full" && (
          <TitleCardsPanel
            project={project}
            onPatchTitleCards={onPatchTitleCards}
          />
        )}
      </main>

      {/* --- Modus-Wechsel-Dialog (Phase 11K Part 2) --- */}
      {modeDialogOpen && project && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto bg-black/75 backdrop-blur-sm"
          onClick={closeModeDialog}
          data-testid="editor-mode-switch-modal"
        >
          <div
            className="relative w-full max-w-3xl rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Rendering-Modus ändern</h2>
            <p className="text-sm text-[#9a9aa3] mt-1">
              Wähle einen anderen Modus für dieses Projekt. Der aktuelle Modus ist bereits ausgewählt.
            </p>
            <div className="mt-5">
              <ModeSelectorCards
                value={modeDialogTarget}
                onSelect={(id) => {
                  setModeDialogTarget(id);
                  applyModeChange(id, false);
                }}
                currentMode={project.render_mode}
                testidPrefix="editor-mode-switch-card"
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeModeDialog}
                disabled={modeDialogBusy}
                data-testid="editor-mode-switch-cancel-btn"
                className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Confirm-Purge-Warnung (nested) --- */}
      {modePurgePrompt && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 overflow-y-auto bg-black/80 backdrop-blur-sm"
          data-testid="editor-mode-switch-confirm-modal"
        >
          <div className="relative w-full max-w-md rounded-2xl bg-[#17171a] border border-amber-500/40 p-6 my-auto">
            <div className="flex items-start gap-3">
              <AlertTriangle size={22} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-amber-100">
                  Achtung: Features werden gelöscht
                </h3>
                <p className="text-sm text-[#c9c9d1] mt-2">
                  Beim Wechsel zu <strong className="text-white">
                    {modePurgePrompt.target === "fastcut"
                      ? "Schnellschnitt"
                      : modePurgePrompt.target === "hybrid"
                        ? "Übergänge"
                        : "Voll-Editor"}
                  </strong> gehen folgende Elemente verloren:
                </p>
                <ul className="mt-3 space-y-1 text-sm text-amber-200">
                  {modePurgePrompt.affected.title_cards > 0 && (
                    <li>• {modePurgePrompt.affected.title_cards} Titelkarte(n)</li>
                  )}
                  {modePurgePrompt.affected.transitions > 0 && (
                    <li>• {modePurgePrompt.affected.transitions} Übergang/Übergänge</li>
                  )}
                  {modePurgePrompt.affected.text_overlays > 0 && (
                    <li>• {modePurgePrompt.affected.text_overlays} Text-Overlay(s)</li>
                  )}
                </ul>
                <p className="text-sm text-[#9a9aa3] mt-3">Fortfahren?</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setModePurgePrompt(null)}
                disabled={modeDialogBusy}
                className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => applyModeChange(modePurgePrompt.target, true)}
                disabled={modeDialogBusy}
                data-testid="editor-mode-switch-confirm-btn"
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors disabled:opacity-50"
              >
                {modeDialogBusy && <Loader2 size={16} className="animate-spin" />}
                Ja, wechseln
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Precheck-Overlay vor Rendern (Phase 11K Part 2) --- */}
      {precheckOpen && project && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto bg-black/75 backdrop-blur-sm"
          onClick={closePrecheck}
          data-testid="editor-precheck-modal"
        >
          <div
            className="relative w-full max-w-lg rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {precheckLoading ? (
              // Loading path — no data-compatible attribute yet, so tests
              // don't get confused by a stale value. This is what the
              // user always sees FIRST while GET /precheck is in flight.
              <div className="py-8 text-center text-[#9a9aa3]">
                <Loader2 size={24} className="animate-spin mx-auto mb-3" />
                <div className="text-sm">Prüfe Clip-Kompatibilität …</div>
              </div>
            ) : precheckError ? (
              // API failed — behave as compatible=false, offer retry.
              <div>
                <div
                  data-testid="editor-precheck-status"
                  data-compatible="false"
                  className="flex items-center gap-2 text-amber-300"
                >
                  <AlertTriangle size={20} />
                  <span className="text-base font-semibold">
                    Kompatibilitäts-Prüfung fehlgeschlagen
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#c9c9d1]">{precheckError}</p>
                <p className="mt-3 text-xs text-[#9a9aa3]">
                  Bitte erneut versuchen. Falls das Problem bestehen bleibt, lade die Seite neu.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={closePrecheck}
                    data-testid="editor-precheck-cancel-btn"
                    className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={retryPrecheck}
                    data-testid="editor-precheck-retry-btn"
                    className="inline-flex items-center justify-center h-10 px-4 rounded-lg bg-[#ff6b35] hover:bg-[#e55a24] text-white font-semibold transition-colors"
                  >
                    Erneut prüfen
                  </button>
                </div>
              </div>
            ) : precheckData && precheckData.compatible ? (
              // Real compatibility from the backend — ONLY reachable when
              // GET /precheck actually returned compatible=true. The
              // Rendern-starten-Button now cannot appear falsely.
              <div>
                <div
                  data-testid="editor-precheck-status"
                  data-compatible="true"
                  className="flex items-center gap-2 text-emerald-300"
                >
                  <CheckCircle2 size={20} />
                  <span className="text-base font-semibold">Alles bereit</span>
                </div>
                <div className="mt-4 space-y-2 text-sm text-[#c9c9d1]">
                  <div>
                    Modus:{" "}
                    <strong className="text-white">
                      {precheckData.mode === "fastcut"
                        ? "Schnellschnitt"
                        : precheckData.mode === "hybrid"
                          ? "Übergänge"
                          : "Voll-Editor"}
                    </strong>
                  </div>
                  <div>
                    {precheckData.clip_specs.length} Clip
                    {precheckData.clip_specs.length === 1 ? "" : "s"}
                    {precheckData.clip_specs[0]?.width && (
                      <>
                        {" · "}alle mit{" "}
                        <strong className="text-white">
                          {precheckData.clip_specs[0].width}×{precheckData.clip_specs[0].height}
                        </strong>
                      </>
                    )}
                  </div>
                  <div className="text-[#9a9aa3]">
                    Erwartete Renderdauer:{" "}
                    <strong className="text-white">
                      {formatEstimate(estimateSecondsFor(precheckData.mode, totalClipDurSec))}
                    </strong>
                  </div>
                </div>
                {precheckData.notices && precheckData.notices.length > 0 && (
                  <ul
                    className="mt-4 space-y-1.5 text-sm text-sky-100 bg-sky-500/5 border border-sky-500/30 rounded-md px-3 py-2.5"
                    data-testid="editor-precheck-notices"
                  >
                    {precheckData.notices.map((n, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-sky-300 mt-0.5">•</span>
                        <span className="flex-1 leading-relaxed">{n}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closePrecheck}
                    data-testid="editor-precheck-cancel-btn"
                    className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={confirmAndStartRender}
                    data-testid="editor-precheck-render-btn"
                    className="inline-flex items-center justify-center h-10 px-5 rounded-lg bg-[#ff6b35] hover:bg-[#e55a24] text-white font-semibold transition-colors"
                  >
                    Rendern starten
                  </button>
                </div>
              </div>
            ) : precheckData ? (
              // Backend says NOT compatible. Two sub-cases: no-clips
              // (special handling — user must add clips first) vs.
              // clip-mismatch (offer full-mode fallback).
              (() => {
                const isNoClips = precheckData.reason === "no_clips";
                return (
                  <div>
                    <div
                      data-testid="editor-precheck-status"
                      data-compatible="false"
                      className="flex items-center gap-2 text-amber-300"
                    >
                      <AlertTriangle size={20} />
                      <span className="text-base font-semibold">
                        {isNoClips
                          ? "Keine Clips im Projekt"
                          : `Nicht bereit für ${
                              precheckData.mode === "fastcut"
                                ? "Schnellschnitt"
                                : precheckData.mode === "hybrid"
                                  ? "Übergänge"
                                  : "Voll-Editor"
                            }`}
                      </span>
                    </div>
                    {isNoClips ? (
                      <p className="mt-3 text-sm text-[#c9c9d1]">
                        Dieses Projekt hat keine Clips. Bitte zuerst über die Bibliothek
                        Clips zur Timeline hinzufügen.
                      </p>
                    ) : (
                      <ul className="mt-4 space-y-1 text-sm text-amber-200">
                        {precheckData.issues.map((iss, i) => (
                          <li key={i}>• {iss}</li>
                        ))}
                      </ul>
                    )}
                    {!isNoClips && (
                      <div className="mt-5 text-xs text-[#9a9aa3]">Optionen:</div>
                    )}
                    <div className="mt-2 flex flex-col gap-2">
                      {/* Switch-to-full only makes sense when we actually
                          HAVE clips that mismatch. No point offering it
                          when the project is simply empty. */}
                      {!isNoClips && precheckData.mode !== "full" && (
                        <button
                          onClick={switchToFullFromPrecheck}
                          disabled={precheckSwitching}
                          data-testid="editor-precheck-switch-full-btn"
                          className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-[#1f1f23] border border-[#2a2a30] hover:border-[#3a3a44] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {precheckSwitching && <Loader2 size={16} className="animate-spin" />}
                          Zu Voll-Editor wechseln (rendert alles)
                        </button>
                      )}
                      <button
                        onClick={closePrecheck}
                        className="inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
                        data-testid="editor-precheck-cancel-btn"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
